/**
 * Submission Upload Dialog Component
 * Allows students to upload files or paste text for assessments
 */

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Paper,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CodeIcon from '@mui/icons-material/Code';
import axios from 'axios';

interface SubmissionUploadDialogProps {
  open: boolean;
  onClose: () => void;
  assessmentId: number;
  assessmentTitle: string;
  submissionFormat: 'file' | 'text' | 'both';
  allowedFileTypes?: string[] | null;
  onSuccess?: () => void;
}

const SubmissionUploadDialog: React.FC<SubmissionUploadDialogProps> = ({
  open,
  onClose,
  assessmentId,
  assessmentTitle,
  submissionFormat,
  allowedFileTypes,
  onSuccess,
}) => {
  // Default file types if none specified
  const DEFAULT_FILE_TYPES = ['.pdf', '.txt', '.md', '.html', '.json', '.py', '.js', '.java', '.cpp', '.c', '.cs', '.ts', '.tsx', '.jsx'];

  // Use provided file types or default
  const acceptedFileTypes = allowedFileTypes && allowedFileTypes.length > 0
    ? allowedFileTypes
    : DEFAULT_FILE_TYPES;

  const acceptString = acceptedFileTypes.join(',');

  // Format file types for display
  const formatFileTypesForDisplay = () => {
    if (acceptedFileTypes.length <= 4) {
      return acceptedFileTypes.map(ft => ft.toUpperCase().replace('.', '')).join(', ');
    } else {
      const first3 = acceptedFileTypes.slice(0, 3).map(ft => ft.toUpperCase().replace('.', ''));
      return `${first3.join(', ')}, and ${acceptedFileTypes.length - 3} more`;
    }
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textContent, setTextContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 for file, 1 for text
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      setError(null);
      setSuccess(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setError(null);
      setSuccess(false);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const detectContentType = (filename: string): string => {
    const extension = filename.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'pdf';
      case 'txt':
      case 'md':
        return 'text';
      case 'json':
        return 'json';
      case 'xml':
      case 'csv':
        return 'data';
      case 'html':
      case 'htm':
        return 'html';
      case 'docx':
      case 'doc':
        return 'document';
      case 'py':
      case 'js':
      case 'java':
      case 'cpp':
      case 'c':
      case 'cs':
      case 'ts':
      case 'tsx':
      case 'jsx':
      case 'go':
      case 'rs':
      case 'rb':
      case 'php':
      case 'sql':
      case 'sh':
      case 'ipynb':
        return 'code';
      default:
        return 'pdf'; // Default fallback
    }
  };

  const handleUpload = async () => {
    const isTextMode = submissionFormat === 'text' || (submissionFormat === 'both' && activeTab === 1);

    // Validation
    if (isTextMode) {
      if (!textContent || textContent.trim() === '') {
        setError('Please enter your submission text');
        return;
      }
    } else {
      if (!selectedFile) {
        setError('Please select a file to upload');
        return;
      }
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      if (isTextMode) {
        // Text submission
        const payload = {
          assessment_id: assessmentId,
          text_content: textContent,
          content_type: 'text',
        };

        await axios.post('/api/submissions/', payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        setUploadProgress(100);
      } else {
        // File submission
        const formData = new FormData();
        formData.append('file', selectedFile!);
        formData.append('assessment_id', assessmentId.toString());
        formData.append('content_type', detectContentType(selectedFile!.name));

        await axios.post('/api/submissions/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percentCompleted);
            }
          },
        });
      }

      setSuccess(true);
      setUploadProgress(100);

      // Wait a moment to show success message
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Upload error:', err);

      // Check for "already submitted" error
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.response?.data) {
        // Handle validation errors
        const errors = Object.entries(err.response.data)
          .map(([key, value]) => {
            // Handle nested error objects
            if (typeof value === 'object' && value !== null) {
              return JSON.stringify(value);
            }
            return `${key}: ${value}`;
          })
          .join(', ');
        setError(errors);
      } else {
        setError('Failed to upload submission. Please try again.');
      }
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setTextContent('');
      setError(null);
      setSuccess(false);
      setUploadProgress(0);
      setActiveTab(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Submit Assignment</Typography>
          <IconButton onClick={handleClose} disabled={uploading} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {assessmentTitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            Submission uploaded successfully!
          </Alert>
        )}

        {/* Tabs for 'both' mode */}
        {submissionFormat === 'both' && (
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
            <Tab label="Upload File" icon={<CloudUploadIcon />} iconPosition="start" />
            <Tab label="Paste Text/Code" icon={<CodeIcon />} iconPosition="start" />
          </Tabs>
        )}

        {/* File Upload UI */}
        {(submissionFormat === 'file' || (submissionFormat === 'both' && activeTab === 0)) && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              cursor: selectedFile ? 'default' : 'pointer',
              backgroundColor: dragActive ? 'action.hover' : 'background.paper',
              borderColor: dragActive ? 'primary.main' : 'divider',
              borderWidth: 2,
              borderStyle: 'dashed',
              transition: 'all 0.2s',
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={!selectedFile ? handleBrowseClick : undefined}
          >
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={handleFileSelect}
              accept={acceptString}
            />

            {!selectedFile ? (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Drag and drop your file here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Supported: {formatFileTypesForDisplay()}
                </Typography>
              </Box>
            ) : (
              <Box>
                <InsertDriveFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
                <Box mt={2}>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile();
                    }}
                    disabled={uploading}
                  >
                    Remove File
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Text/Code Input UI */}
        {(submissionFormat === 'text' || (submissionFormat === 'both' && activeTab === 1)) && (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Paste your code or text here..."
              disabled={uploading}
              sx={{
                fontFamily: 'monospace',
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" mt={1} display="block">
              {textContent.length} characters
            </Typography>
          </Box>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={
            (submissionFormat === 'file' || (submissionFormat === 'both' && activeTab === 0)
              ? !selectedFile
              : !textContent.trim()) ||
            uploading ||
            success
          }
          startIcon={uploading ? null : <CloudUploadIcon />}
        >
          {uploading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmissionUploadDialog;
